import React, { useContext, useState } from 'react';
import {
  Drawer,
  makeStyles,
  Typography,
  IconButton,
  FormGroup,
  InputLabel,
  Button,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DoneIcon from '@material-ui/icons/Done';
import { pink } from '@material-ui/core/colors';

import validator from 'validator';
import moment from 'moment';

import { AppContext } from '../context/AppContext';
import { AuthContext } from '../context/AuthContext';

import { auth, db } from '../utils/firebase';
import { UsersContext } from '../context/UsersContext';

const useStyles = makeStyles({
  drawer: {
    width: '400px',
    height: '100%',
    position: 'relative',
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
  },
  user_image: {
    height: '300px',
    backgroundColor: pink[500],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  user_info: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    padding: '5px 15px',
  },
  title_container: {
    display: 'flex',
    alignItems: 'center',
  },
  textWhite: {
    color: '#fff',
  },
  sm_container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sm_container2: {
    padding: '0px 15px',
    overflowY: 'auto',
  },
  formGroup: {
    marginTop: '15px',
  },
  btn_container: {
    marginTop: '45px',
  },
  btn: {
    margin: '5px 0',
    width: '100%',
  },
  inputEdit: {
    marginTop: '10px',
  },
});

export default function Sidebar() {
  const { currentUser } = useContext(AuthContext);
  const { sidebar: _sidebar, snack: _snack } = useContext(AppContext);
  const { USERS } = useContext(UsersContext);

  const [sidebar, setSidebar] = _sidebar;
  const [isLoading, setIsLoading] = useState(false);
  const setSnack = _snack[1];
  const [editUsername, setEditUsername] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [username, setUsername] = useState(currentUser.username);
  const [email, setEmail] = useState(currentUser.email);
  const [usernameError, setUsernameError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  const classes = useStyles();

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'email') {
      setEmail(value);

      if (!value) {
        setEmailError('Email is required');
      } else if (!validator.isEmail(value)) {
        setEmailError('Email is invalid');
      } else {
        setEmailError(null);
      }
    } else if (name === 'username') {
      setUsername(value);

      if (!value) {
        setUsernameError('Username is required');
      } else if (value.trim().length < 3 || value.trim().length > 16) {
        setUsernameError('Username must be 3-16 characters');
      } else if (
        value.trim().toLowerCase() === 'admin' ||
        value.trim().toLowerCase() === 'administrator'
      ) {
        setUsernameError('Username is restricted');
      } else {
        setUsernameError(null);
      }
    }
  };

  const changeUsername = () => {
    setIsLoading(true);

    const promise1 = auth.currentUser.updateProfile({
      displayName: username,
    });

    const promise2 = db.collection('users').doc(currentUser.uid).update({
      username,
    });

    Promise.all([promise1, promise2])
      .then(() => {
        setEditUsername(false);
        setUsername(auth.currentUser.displayName);
        setEmail(auth.currentUser.email);
        setIsLoading(false);
        setSnack({
          open: true,
          status: 'success',
          message: 'Username updated sucessfully',
        });
      })
      .catch((err) => {
        //console.log(err);
        setIsLoading(false);
        setSnack({
          open: true,
          status: 'error',
          message: 'Failed to update username! Please try again',
        });
      });
  };

  const changeEmail = () => {
    setIsLoading(true);

    const promise1 = auth.currentUser.updateEmail(email);

    const promise2 = db.collection('users').doc(currentUser.uid).update({
      email,
    });

    Promise.all([promise1, promise2])
      .then(() => {
        setEditEmail(false);
        setEmail('');
        setIsLoading(false);
        setSnack({
          open: true,
          status: 'success',
          message: 'Email updated sucessfully',
        });
      })
      .catch((err) => {
        //console.log(err)
        setIsLoading(false);
        if (err.code === 'auth/requires-recent-login') {
          setSnack({
            open: true,
            status: 'warning',
            message:
              'Security alert! You must log in again before you can complete this action',
          });
        } else {
          setSnack({
            open: true,
            status: 'error',
            message: 'Failed to update email! Please try again',
          });
        }
      });
  };

  const sendResetLink = () => {
    setIsLoading(true);
    auth
      .sendPasswordResetEmail(auth.currentUser.email)
      .then(function () {
        // Email sent.
        setIsLoading(false);
        setSnack({
          open: true,
          status: 'success',
          message: 'A reset link has been sent to your email address',
        });
      })
      .catch(function (err) {
        // An error happened.
        setIsLoading(false);
        setSnack({
          open: true,
          status: 'error',
          message: 'An error occured! Please try again',
        });
      });
  };
  const self = USERS.filter((user) => user.uid === currentUser.uid)[0];

  if (!currentUser.username || !self) {
    return null;
  }

  return (
    <Drawer anchor='right' open={sidebar} onClose={() => setSidebar(false)}>
      <div className={classes.drawer}>
        <div className={classes.title_container}>
          <IconButton onClick={() => setSidebar(false)}>
            <CloseIcon />
          </IconButton>
          <Typography>Profile</Typography>
          {isLoading && (
            <CircularProgress
              size={16}
              color='secondary'
              style={{ margin: '0 15px 0 auto' }}
            />
          )}
        </div>
        <div className={classes.user_image}>
          <Typography className={classes.textWhite} variant='h1'>
            {auth.currentUser.displayName[0].toUpperCase()}
          </Typography>
          <div className={classes.user_info}>
            <Typography className={classes.textWhite}>
              {auth.currentUser.displayName}
            </Typography>
            <Typography style={{ color: '#ddd' }} variant='body2'>
              Joined {moment.utc(self.joined).local().fromNow()}
            </Typography>
          </div>
        </div>
        <div className={classes.sm_container2}>
          <FormGroup className={classes.formGroup}>
            <InputLabel>Username</InputLabel>
            <div className={classes.sm_container}>
              {!editUsername ? (
                <>
                  <Typography>{auth.currentUser.displayName}</Typography>
                  <IconButton
                    onClick={() => {
                      setEditUsername(true);
                      setEditEmail(false);
                    }}
                    disabled={isLoading}
                  >
                    <EditIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    onChange={handleChange}
                    name='username'
                    value={username}
                    error={usernameError ? true : false}
                    helperText={usernameError}
                    disabled={isLoading}
                  />

                  <IconButton
                    onClick={() => setEditUsername(false)}
                    disabled={isLoading}
                  >
                    <CloseIcon />
                  </IconButton>
                  <IconButton
                    disabled={
                      !username || usernameError || isLoading ? true : false
                    }
                    onClick={() => changeUsername()}
                  >
                    <DoneIcon />
                  </IconButton>
                </>
              )}
            </div>
          </FormGroup>
          <FormGroup className={classes.formGroup}>
            <InputLabel>Email</InputLabel>
            <div className={classes.sm_container}>
              {!editEmail ? (
                <>
                  <Typography>{auth.currentUser.email}</Typography>
                  <IconButton
                    onClick={() => {
                      setEditEmail(true);
                      setEditUsername(false);
                    }}
                    disabled={isLoading}
                  >
                    <EditIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    onChange={handleChange}
                    name='email'
                    value={email}
                    error={emailError ? true : false}
                    helperText={emailError}
                    disabled={isLoading}
                  />
                  <IconButton
                    onClick={() => setEditEmail(false)}
                    disabled={isLoading}
                  >
                    <CloseIcon />
                  </IconButton>
                  <IconButton
                    disabled={!email || emailError || isLoading ? true : false}
                    onClick={() => changeEmail()}
                  >
                    <DoneIcon />
                  </IconButton>
                </>
              )}
            </div>
          </FormGroup>
          <div className={classes.btn_container}>
            <Button
              variant='outlined'
              color='primary'
              className={classes.btn}
              onClick={() => sendResetLink()}
              disabled={isLoading}
            >
              Reset password
            </Button>
            <Button
              variant='outlined'
              color='secondary'
              className={classes.btn}
              onClick={() => {
                setSidebar(false);
                auth.signOut();
              }}
              disabled={isLoading}
            >
              Log Out
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
